import { LIVE_RULES, type Alarm } from "@fts/core";
import { useEffect, useMemo, useState } from "react";
import { HelpTip } from "../components/HelpTip";
import { Icon } from "../components/Icon";
import { EmptyState, Loading } from "../components/ui";
import { useI18n, type MessageKey } from "../i18n";
import { alarmTitle, alarmTone, formatAlarmTime } from "../lib/alarms";
import { descendantIds } from "../lib/fleet";
import { formatPercent } from "../lib/format";
import { canManageFleet } from "../lib/permissions";
import { buildLiveVehicles, countByFilter, type LiveFilter } from "../lib/live";
import { buildMenu, type NavNode } from "../nav/menu";
import { useAlarmCount } from "../state/alarms";
import { useSession, type TenantData } from "../state/session";
import { useLive } from "../state/useLive";

/** Motion states in display order; "alarm" cuts across them and is shown separately. */
export const FLEET_STATES = ["working", "idle", "off", "offline"] as const satisfies readonly LiveFilter[];

/** How many open alarms the overview lists. */
export const OVERVIEW_ALARM_LIMIT = 5;

/**
 * Whole-number shares (0–100) that always add up to 100 (largest-remainder method), so the four state
 * percentages never read 99 or 101. All zeros stay zeros.
 */
export function percentShares(counts: number[]): number[] {
  const total = counts.reduce((a, b) => a + b, 0);
  if (total <= 0) return counts.map(() => 0);
  const exact = counts.map((c) => (c * 100) / total);
  const out = exact.map(Math.floor);
  let left = 100 - out.reduce((a, b) => a + b, 0);
  const order = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    out[i]! += 1;
    left -= 1;
  }
  return out;
}

/** Menu entries offered as quick links, in this order (only those the role can open). */
const QUICK_LINK_IDS = ["map", "alarms", "vehicles", "reportUsage", "reportEvents", "checklistFaults"] as const;

/**
 * Lower half of the overview (both tabs): the fleet's live state counts, the newest open alarms and
 * quick links. Uses the same live feed and alarm list as the live map and the alarm screen; no numbers
 * of its own.
 */
export function OverviewLive({ data, siteId = null }: { data: TenantData; /** KPI panel site filter (with sub-sites); null = all. */ siteId?: string | null }) {
  const { t } = useI18n();
  const { role, platformAdmin } = useSession();
  const siteIds = useMemo(() => (siteId ? descendantIds(data.sites, siteId) : null), [data.sites, siteId]);
  const links = useMemo(() => {
    if (!role) return [];
    const flat: NavNode[] = [];
    const walk = (ns: NavNode[]) => ns.forEach((n) => (n.kind === "item" && flat.push(n), n.children && n.kind === "group" && walk(n.children)));
    walk(buildMenu([], role, platformAdmin));
    return QUICK_LINK_IDS.map((id) => flat.find((n) => n.id === id)).filter((n): n is NavNode => !!n);
  }, [role, platformAdmin]);

  return (
    <>
      <div className="overview-grid">
        <FleetStatus data={data} siteIds={siteIds} />
        <LatestAlarms data={data} siteIds={siteIds} />
      </div>
      {links.length > 0 && (
        <nav className="quick-links" aria-label={t("overview.quick.title")}>
          <h2 className="quick-links-title">{t("overview.quick.title")}</h2>
          <ul>
            {links.map((n) => (
              <li key={n.id}>
                <a href={n.href} className="quick-link">
                  {n.icon && <Icon name={n.icon} size={18} />}
                  <span>{t(n.labelKey!)}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  );
}

function FleetStatus({ data, siteIds }: { data: TenantData; siteIds: Set<string> | null }) {
  const { t, locale } = useI18n();
  const { provider, tenant, role } = useSession();
  const live = useLive(provider, tenant!.id);
  const vehicles = useMemo(() => (siteIds ? data.vehicles.filter((v) => siteIds.has(v.siteId)) : data.vehicles), [data.vehicles, siteIds]);
  const list = useMemo(
    () => buildLiveVehicles(vehicles, live.devices ?? data.devices, live.snapshots, live.now),
    [vehicles, data.devices, live.devices, live.snapshots, live.now],
  );
  const counts = countByFilter(list);
  const total = counts.all;
  const untracked = vehicles.length - total;
  const shares = percentShares(FLEET_STATES.map((s) => counts[s]));

  return (
    <section className="panel fleet-status" aria-labelledby="fleet-status-title">
      <header className="panel-header">
        <span className="panel-title-row">
          <h2 className="panel-title" id="fleet-status-title">
            {t("overview.fleet.title")}
          </h2>
          <HelpTip topic={t("help.fleetStates.topic")} align="start">
            {t("help.fleetStates", { offline: Math.round(LIVE_RULES.offlineAfterMs / 60_000), kmh: LIVE_RULES.idleBelowKmh })}
          </HelpTip>
        </span>
        <a className="panel-link" href="#/map">
          {t("overview.fleet.openMap")}
        </a>
      </header>
      {!live.loaded ? (
        <Loading rows={3} />
      ) : total === 0 ? (
        <EmptyState
          compact
          icon="truck"
          title={t("overview.fleet.emptyTitle")}
          action={
            canManageFleet(role) ? (
              <a className="button button-secondary button-sm" href="#/devices">
                {t("overview.fleet.emptyAction")}
              </a>
            ) : undefined
          }
        >
          {t("overview.fleet.emptyBody")}
        </EmptyState>
      ) : (
        <>
          <p className="fleet-total">
            <span className="fleet-total-value">{total.toLocaleString(locale)}</span>
            <span className="fleet-total-label">{t("overview.fleet.tracked")}</span>
            {counts.alarm > 0 && (
              <a className="badge tone-crit fleet-alarm" href="#/alarms">
                <span className="live-dot live-alarm" aria-hidden="true" />
                {t("overview.fleet.inAlarm", { count: counts.alarm })}
              </a>
            )}
          </p>
          {/* Proportional bar; decorative, the list below carries the numbers. */}
          <div className="state-bar" aria-hidden="true">
            {FLEET_STATES.filter((s) => counts[s] > 0).map((s) => (
              <span key={s} className={`state-bar-seg seg-${s}`} style={{ flexGrow: counts[s] }} />
            ))}
          </div>
          <ul className="state-list">
            {FLEET_STATES.map((s, i) => (
              <li key={s} className={counts[s] === 0 ? "is-zero" : undefined}>
                <span className={`live-dot live-${s}`} aria-hidden="true" />
                <span className="state-name">{t(`map.state.${s}` as MessageKey)}</span>
                <span className="state-count">{counts[s].toLocaleString(locale)}</span>
                <span className="state-pct">{formatPercent(shares[i]!, locale, 0)}</span>
              </li>
            ))}
          </ul>
          {untracked > 0 && <p className="fleet-foot">{t("overview.fleet.untracked", { count: untracked })}</p>}
          {live.error && (
            <p className="form-error" role="alert">
              {t("overview.fleet.error")}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function LatestAlarms({ data, siteIds }: { data: TenantData; siteIds: Set<string> | null }) {
  const { t, locale } = useI18n();
  const { provider, tenant } = useSession();
  const { version } = useAlarmCount();
  const [state, setState] = useState<{ alarms: Alarm[]; loaded: boolean; error: boolean }>({ alarms: [], loaded: false, error: false });
  const tenantId = tenant?.id;
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    provider
      .listAlarms(tenantId, { status: "open", siteIds: siteIds ? [...siteIds] : null, limit: OVERVIEW_ALARM_LIMIT })
      .then((alarms) => !cancelled && setState({ alarms, loaded: true, error: false }))
      .catch(() => !cancelled && setState((s) => ({ ...s, loaded: true, error: true })));
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, version, siteIds]);
  const fleetNo = (id: string | null) => (id ? data.vehicles.find((v) => v.id === id)?.fleetNo : undefined);
  const siteName = (id: string | null) => (id ? data.sites.find((s) => s.id === id)?.name : undefined);

  return (
    <section className="panel latest-alarms" aria-labelledby="latest-alarms-title">
      <header className="panel-header">
        <h2 className="panel-title" id="latest-alarms-title">
          {t("overview.alarms.title")}
        </h2>
        <a className="panel-link" href="#/alarms">
          {t("overview.alarms.all")}
        </a>
      </header>
      {!state.loaded ? (
        <Loading rows={4} />
      ) : state.error ? (
        <p className="form-error" role="alert">
          {t("alarms.loadError")}
        </p>
      ) : state.alarms.length === 0 ? (
        <EmptyState compact icon="check" title={t("overview.alarms.emptyTitle")}>
          {t("overview.alarms.emptyBody")}
        </EmptyState>
      ) : (
        <ul className="alarm-feed">
          {state.alarms.map((a) => {
            const time = formatAlarmTime(a.occurredAt, locale) ?? t("common.empty");
            const fn = fleetNo(a.vehicleId);
            return (
              <li key={a.id}>
                <a href={`#/alarms/${encodeURIComponent(a.id)}`} className="alarm-feed-row" aria-label={t("alarms.rowLink", { type: alarmTitle(t, a), time })}>
                  <span className={`status-dot tone-alarm-${alarmTone(a)}`} aria-hidden="true" />
                  <span className="alarm-feed-main">
                    <span className="alarm-feed-title">{alarmTitle(t, a)}</span>
                    <span className="alarm-feed-meta">{[siteName(a.siteId), time].filter(Boolean).join(" · ")}</span>
                  </span>
                  {fn && <span className="mono alarm-feed-vehicle">{fn}</span>}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
